/**
 * Procedural art atlas — Sky Archipelago.
 *
 * Every shape is constructed here from primitives, so the whole look is
 * original by construction: nothing sampled, traced or referenced from
 * any existing game (Iron Rule 7a).
 *
 * Design rules the shapes follow:
 *  - Silhouette first. Each creature reads as a distinct outline in one
 *    frame at thumb size, because colour is unavailable during a hit
 *    flash and unreliable against a busy background.
 *  - Creatures are drawn WHITE and tinted at draw time. Colouring the
 *    texture makes a white hit-flash a no-op.
 *  - Everything is drawn facing +y (up-screen), matching world space, so
 *    rotation is only ever applied for motion, never for correction.
 */
import { Graphics, type Renderer, type Texture } from 'pixi.js';

/** Per-enemy-type tint, applied at draw time over the white silhouette. */
export const ENEMY_TINT = [0xf5372c, 0xb04ae8, 0x25c98a] as const;
export const ENEMY_DARK = [0x8c2b22, 0x5a2a8c, 0x1c7a55] as const;

export interface Textures {
  squad: Texture;
  squadWing: Texture;
  enemy: Texture[];
  boss: Texture;
  bossCore: Texture;
  bullet: Texture[];
  tracer: Texture;
  muzzle: Texture;
  enemyBullet: Texture;
  gem: Texture;
  weapon: Texture;
  particle: Texture;
  chunk: Texture[];
  ring: Texture;
  shadow: Texture;
  gate: Texture;
  cloud: Texture[];
  isle: Texture[];
  glow: Texture;
  sun: Texture;
  ridge: Texture[];
  spire: Texture;
}

const bake = (r: Renderer, g: Graphics, resolution = 3): Texture => {
  const t = r.generateTexture({ target: g, resolution, antialias: true });
  g.destroy();
  return t;
};

/* ------------------------------------------------------------------ hero */

/** Skyguard: helmeted figure with a forward lance. Reads as "pointy, mine". */
function squadFigure(): Graphics {
  const g = new Graphics();
  // lance, drawn first so the body overlaps it
  g.poly([0.6, 13, 2.2, 4, -1, 4]).fill(0xdfe9ff);
  // cloak
  g.poly([-6.5, -8, 0, -3, 6.5, -8, 4.5, 3, -4.5, 3]).fill(0x2f6ee0);
  // torso
  g.roundRect(-3.4, -5, 6.8, 9, 2.6).fill(0x4f92ff);
  // pauldrons
  g.circle(-4.2, 1.5, 2.3).fill(0x2a5fc4);
  g.circle(4.2, 1.5, 2.3).fill(0x2a5fc4);
  // helm
  g.circle(0, 5.2, 3.5).fill(0x8fc4ff);
  g.poly([-3.4, 5.6, 3.4, 5.6, 0, 9.6]).fill(0xffd76b); // crest
  // visor
  g.roundRect(-2.2, 4.2, 4.4, 2.1, 1).fill(0x2b3f66);
  return g;
}

function squadWing(): Graphics {
  const g = new Graphics();
  g.poly([0, 0, -9, 4.5, -12, -1, -7, -4]).fill({ color: 0xbcd8ff, alpha: 0.85 });
  g.poly([0, 0, -7, 2.5, -9, -0.5]).fill({ color: 0x9fc4ff, alpha: 0.9 });
  return g;
}

/* --------------------------------------------------------------- enemies */

/** Rift Skitterer: low, many-legged, aggressive wedge. */
function skitterer(): Graphics {
  const g = new Graphics();
  for (const s of [-1, 1]) {
    g.poly([s * 2, 1, s * 9, 5, s * 7.5, 6.2, s * 1.5, 2.6]).fill(0xffffff);
    g.poly([s * 2, -1, s * 9.5, -2, s * 8, -3.6, s * 1.5, -2.4]).fill(0xffffff);
    g.poly([s * 2, -3, s * 7.5, -7.5, s * 6, -8.6, s * 1.5, -4.6]).fill(0xffffff);
  }
  g.poly([0, 8.5, 5.2, 0, 2.6, -6.5, -2.6, -6.5, -5.2, 0]).fill(0xffffff);
  g.poly([0, 9.5, 2.4, 4.5, -2.4, 4.5]).fill(0xffffff);
  g.circle(0, 2.2, 2.5).fill(0xdcdcdc); // eye socket, darkens under tint
  return g;
}

/** Gale Wretch: hooded drifter with a tattered trail. Tall, slow, heavy. */
function wretch(): Graphics {
  const g = new Graphics();
  g.poly([-5.5, -2, 5.5, -2, 3.5, -11, 1.5, -6, 0, -12, -1.5, -6, -3.5, -11]).fill(0xffffff);
  g.poly([-7, 1, 7, 1, 5.5, -3, -5.5, -3]).fill(0xffffff);
  g.circle(0, 3.5, 6.4).fill(0xffffff);
  g.poly([-6.4, 3.2, 6.4, 3.2, 0, 11.5]).fill(0xffffff);
  g.circle(-2.2, 4.2, 1.5).fill(0xd0d0d0);
  g.circle(2.2, 4.2, 1.5).fill(0xd0d0d0);
  return g;
}

/** Mote Slinger: crystalline turret. Angular, static, ranged. */
function slinger(): Graphics {
  const g = new Graphics();
  g.poly([0, 7.5, 6.5, 3.5, 6.5, -3.5, 0, -7.5, -6.5, -3.5, -6.5, 3.5]).fill(0xffffff);
  g.poly([0, 4.5, 3.8, 2, 3.8, -2, 0, -4.5, -3.8, -2, -3.8, 2]).fill(0xdadada);
  for (const [x, y] of [[-8.5, 5], [8.5, 5], [0, -9.5]]) {
    g.poly([x, y + 2.4, x + 2, y, x, y - 2.4, x - 2, y]).fill(0xffffff);
  }
  return g;
}

/** Stormhulk: layered colossus. Wide, armoured, unmistakably a boss. */
function stormhulk(): Graphics {
  const g = new Graphics();
  g.poly([-26, -18, 26, -18, 20, -26, -20, -26]).fill(0xb98a2e); // base plate
  g.poly([-22, 16, -30, 2, -24, -18, 24, -18, 30, 2, 22, 16]).fill(0xffd76b);
  g.poly([-22, 16, 22, 16, 14, 26, -14, 26]).fill(0xffe9a8); // head shelf
  g.poly([-30, 4, -40, -6, -34, -14, -26, -6]).fill(0xf0c04f); // shoulders
  g.poly([30, 4, 40, -6, 34, -14, 26, -6]).fill(0xf0c04f);
  g.poly([-14, 26, 14, 26, 8, 34, -8, 34]).fill(0xfff2cc); // crown
  g.poly([-8, 34, -14, 46, -4, 36]).fill(0xffd76b); // horns
  g.poly([8, 34, 14, 46, 4, 36]).fill(0xffd76b);
  g.roundRect(-11, 20, 22, 7, 3).fill(0x3a2600); // visor band
  g.poly([-24, -18, 24, -18, 16, -30, -16, -30]).fill(0xd9a53c); // skirt
  return g;
}

/** Boss core: pulsed separately so the phase change has something to say. */
function bossCore(): Graphics {
  const g = new Graphics();
  g.circle(0, 0, 9).fill({ color: 0xffffff, alpha: 0.35 });
  g.circle(0, 0, 5.5).fill(0xffffff);
  g.poly([0, 11, 3.4, 0, 0, -11, -3.4, 0]).fill({ color: 0xffffff, alpha: 0.8 });
  return g;
}

/* ------------------------------------------------------------ projectiles */

/** One projectile look per weapon, so a weapon swap is visible instantly. */
function lance(): Graphics {
  const g = new Graphics();
  g.poly([0, 9, 2.6, 1, 0, -7, -2.6, 1]).fill(0xfff4c2);
  g.poly([0, 6.5, 1.2, 0.5, 0, -3.5, -1.2, 0.5]).fill(0xffffff);
  return g;
}

function scatterMote(): Graphics {
  const g = new Graphics();
  g.circle(0, 0, 3.6).fill(0xbfe9ff);
  g.circle(0, 0, 2).fill(0xffffff);
  g.poly([0, 6, 1.8, 0, 0, -6, -1.8, 0]).fill({ color: 0xbfe9ff, alpha: 0.55 });
  return g;
}

function tracer(): Graphics {
  // Layered bolt: wide warm halo, hot mid, white-hot core. Density and
  // brightness are what make massed fire read as overwhelming force
  // rather than as confetti (teardown E1/E3/E10).
  const g = new Graphics();
  g.poly([0, 2, 4.2, -9, 0, -26, -4.2, -9]).fill({ color: 0xff8a2a, alpha: 0.42 });
  g.poly([0, 0, 2.6, -8, 0, -22, -2.6, -8]).fill({ color: 0xffc24a, alpha: 0.92 });
  g.poly([0, -1, 1.15, -8, 0, -17, -1.15, -8]).fill({ color: 0xfffdf2, alpha: 1 });
  g.circle(0, -1.5, 2.1).fill({ color: 0xffffff, alpha: 0.85 });
  return g;
}

function muzzle(): Graphics {
  const g = new Graphics();
  g.poly([0, 17, 7, 2, 0, 5, -7, 2]).fill({ color: 0xffca55, alpha: 0.95 });
  g.circle(0, 2, 6.2).fill({ color: 0xfffdf2, alpha: 0.95 });
  g.poly([9, 5, 3, 1.5, 9, -2]).fill({ color: 0xffe08a, alpha: 0.7 });
  g.poly([-9, 5, -3, 1.5, -9, -2]).fill({ color: 0xffe08a, alpha: 0.7 });
  return g;
}

/* ------------------------------------------------------------------- fx */

/** Debris chunks. Three sizes so a big kill can throw bigger pieces. */
function chunk(scale: number): Graphics {
  const g = new Graphics();
  g.poly([0, 3.2 * scale, 2.8 * scale, 0.6 * scale, 1.6 * scale, -3 * scale,
    -1.8 * scale, -2.6 * scale, -3 * scale, 0.8 * scale]).fill(0xffffff);
  return g;
}

/** Expanding shockwave ring for heavy deaths. */
function ring(): Graphics {
  const g = new Graphics();
  g.circle(0, 0, 26).stroke({ width: 4, color: 0xffffff, alignment: 0.5 });
  return g;
}

/** Soft contact shadow — the cheapest, strongest depth cue available. */
function shadow(): Graphics {
  const g = new Graphics();
  for (let i = 6; i >= 1; i--) {
    g.ellipse(0, 0, 13 * (i / 6), 5.4 * (i / 6)).fill({ color: 0x000000, alpha: 0.052 });
  }
  return g;
}

/** Sun disc with a wide falloff halo — the scene's key light. */
function sun(): Graphics {
  const g = new Graphics();
  for (let i = 22; i >= 1; i--) {
    const t = i / 22;
    g.circle(0, 0, 26 + t * 150).fill({ color: 0xffe6a8, alpha: 0.028 * (1 - t) + 0.006 });
  }
  g.circle(0, 0, 30).fill({ color: 0xfff6d8, alpha: 0.9 });
  g.circle(0, 0, 21).fill(0xffffff);
  return g;
}

/**
 * A distant ridge line. Drawn as one silhouette so it can be tinted to
 * whatever the atmosphere calls for at that depth — aerial perspective
 * does far more for a sense of scale than detail does.
 */
function ridge(seed: number, peaks: number, height: number): Graphics {
  const g = new Graphics();
  const pts: number[] = [-620, 200];
  let x = -620;
  let n = seed * 2654435761 % 2147483647;
  const rnd = (): number => { n = (n * 1103515245 + 12345) % 2147483647; return n / 2147483647; };
  for (let i = 0; i <= peaks; i++) {
    const h = height * (0.45 + rnd() * 0.55);
    pts.push(x, 200 - h);
    x += 1240 / peaks;
    pts.push(x - 620 / peaks, 200 - h * (0.3 + rnd() * 0.3));
  }
  pts.push(620, 200);
  g.poly(pts).fill(0xffffff);
  return g;
}

/** A floating spire: tall, thin, unmistakably far away and enormous. */
function spire(): Graphics {
  const g = new Graphics();
  g.poly([-26, 300, 26, 300, 16, 40, 34, 0, 8, -60, 0, -150, -10, -60, -32, 0, -16, 40]).fill(0xffffff);
  g.ellipse(0, 300, 54, 16).fill({ color: 0xffffff, alpha: 0.85 });
  return g;
}

function glow(): Graphics {
  const g = new Graphics();
  for (let i = 10; i >= 1; i--) {
    g.circle(0, 0, 26 * (i / 10)).fill({ color: 0xffffff, alpha: 0.035 });
  }
  return g;
}

/* --------------------------------------------------------------- world */

function gate(): Graphics {
  const g = new Graphics();
  g.roundRect(-48, -21, 96, 42, 10).fill({ color: 0xffffff, alpha: 0.1 });
  g.roundRect(-48, -21, 96, 42, 10).stroke({ width: 3, color: 0xffffff });
  g.roundRect(-44, 12, 88, 6, 3).fill({ color: 0xffffff, alpha: 0.35 });
  for (let i = -3; i <= 3; i++) {
    g.circle(i * 13, -21, 2.4).fill({ color: 0xffffff, alpha: 0.8 });
  }
  return g;
}

function cloud(seedShape: number): Graphics {
  const g = new Graphics();
  const lobes = 5 + (seedShape % 3);
  for (let i = 0; i < lobes; i++) {
    const t = i / (lobes - 1) - 0.5;
    const rx = 30 - Math.abs(t) * 34;
    g.ellipse(t * 78, Math.sin(i * 1.7 + seedShape) * 6, Math.max(9, rx), Math.max(7, rx * 0.62))
      .fill({ color: 0xffffff, alpha: 1 });
  }
  return g;
}

/** Distant floating isle: a slab with a rocky underside. */
function isle(w: number): Graphics {
  const g = new Graphics();
  g.ellipse(0, 0, w, w * 0.22).fill(0xffffff);
  g.poly([-w * 0.8, 0, w * 0.8, 0, w * 0.26, w * 0.78, -w * 0.1, w * 0.5, -w * 0.42, w * 0.9])
    .fill({ color: 0xffffff, alpha: 0.82 });
  g.ellipse(-w * 0.2, -w * 0.09, w * 0.5, w * 0.12).fill({ color: 0xffffff, alpha: 0.55 });
  return g;
}

export function buildTextures(r: Renderer): Textures {
  return {
    squad: bake(r, squadFigure()),
    squadWing: bake(r, squadWing()),
    enemy: [bake(r, skitterer()), bake(r, wretch()), bake(r, slinger())],
    boss: bake(r, stormhulk(), 2),
    bossCore: bake(r, bossCore()),
    bullet: [bake(r, lance()), bake(r, scatterMote())],
    tracer: bake(r, tracer()),
    muzzle: bake(r, muzzle()),
    enemyBullet: bake(r, scatterMote()),
    gem: bake(r, (() => {
      const g = new Graphics();
      g.poly([0, 8, 5.5, 0, 0, -8, -5.5, 0]).fill(0x7dffd0);
      g.poly([0, 8, 2.4, 0, 0, -8, -2.4, 0]).fill(0xffffff);
      return g;
    })()),
    weapon: bake(r, (() => {
      const g = new Graphics();
      g.star(0, 0, 6, 10, 4.6).fill(0xffd76b);
      g.star(0, 0, 6, 5, 2.2).fill(0xfff6d8);
      return g;
    })()),
    particle: bake(r, new Graphics().circle(0, 0, 3).fill(0xffffff)),
    chunk: [bake(r, chunk(1)), bake(r, chunk(1.9)), bake(r, chunk(3.2))],
    ring: bake(r, ring(), 2),
    shadow: bake(r, shadow(), 2),
    gate: bake(r, gate(), 2),
    cloud: [bake(r, cloud(0), 1), bake(r, cloud(1), 1), bake(r, cloud(2), 1)],
    isle: [bake(r, isle(60), 1), bake(r, isle(95), 1), bake(r, isle(140), 1)],
    glow: bake(r, glow(), 1),
    sun: bake(r, sun(), 1),
    ridge: [bake(r, ridge(7, 9, 96), 1), bake(r, ridge(23, 6, 150), 1), bake(r, ridge(91, 4, 220), 1)],
    spire: bake(r, spire(), 1),
  };
}
